import {isFeatureEnabled} from '@github-ui/feature-flags'
import {usePortalTooltip} from '@github-ui/portal-tooltip/use-portal-tooltip'
import {testIdProps} from '@github-ui/test-id-props'
import {useLayoutEffect} from '@github-ui/use-layout-effect'
import {Label, Link, Truncate} from '@primer/react'
import {Tooltip} from '@primer/react/deprecated'
import {useTheme} from '@primer/styled-react'
import {clsx} from 'clsx'
import {addDays} from 'date-fns'
import {type ComponentPropsWithoutRef, forwardRef, memo, useRef, useState} from 'react'

import type {TitleValueWithContentType} from '../../../api/columns/contracts/title'
import type {User} from '../../../api/common-contracts'
import useIsVisible from '../../../components/board/hooks/use-is-visible'
import {AssigneeStack} from '../../../components/common/assignee-stack'
import {MemexItemIcon} from '../../../components/common/memex-item-icon'
import {SanitizedHtml} from '../../../components/dom/sanitized-html'
import {
  PILL_PADDING,
  PILL_STICKY_MARGIN,
  ROADMAP_ITEM_GRAB_HANDLE_HEIGHT,
  ROADMAP_ITEM_GRAB_HANDLE_WIDTH,
} from '../../../components/roadmap/constants'
import {useClearTableFocus} from '../../../components/table/hooks/use-clear-table-focus'
import {useUserSettings} from '../../../components/user-settings'
import {formatISODateString} from '../../../helpers/parsing'
import type {TimeSpan} from '../../../helpers/roadmap-helpers'
import useBodyClass from '../../../hooks/use-body-class'
import {useRoadmapTableWidth} from '../../../hooks/use-roadmap-settings'
import type {ColumnValue} from '../../../models/column-value'
import type {MemexItemModel} from '../../../models/memex-item-model'
import {RoadmapResources} from '../../../strings'
import {getSortedDates} from '../date-utils'
import type {MouseState} from '../hooks/use-mouse-state'
import type {
  RoadmapDndOperation,
  RoadmapDragAndDropOperations,
  useRoadmapDragAndDrop,
} from '../hooks/use-roadmap-drag-and-drop'
import {PillAreaFocusType, useRoadmapPillAreaFocus} from '../hooks/use-roadmap-pill-area-focus'
import {useRoadmapGetViewport, useRoadmapView} from '../roadmap-view-provider'
import {getTooltipText} from '../tooltip-helper'
import styles from './roadmap-pill.module.css'
import {NavigationArrowWithFocusContext} from './roadmap-pill-buttons'
import {RoadmapCell} from './roadmap-table-layout'

const RoadmapPillDragHandle = memo(
  forwardRef<
    HTMLDivElement,
    ComponentPropsWithoutRef<'div'> & {
      tooltip: string | undefined
      operation: RoadmapDndOperation
      active?: boolean
      containerRef: React.RefObject<HTMLElement | null>
      isDisabled?: boolean
    }
  >(function RoadmapPillDragHandle({tooltip, operation, active, containerRef, isDisabled, ...props}, forwardedRef) {
    const [isTouchInteraction, setIsTouchInteraction] = useState(false)

    const [contentProps, portalTooltip] = usePortalTooltip({
      contentRef: containerRef,
      'aria-label': tooltip,
      direction: 'n',
      alignmentOffset: -ROADMAP_ITEM_GRAB_HANDLE_HEIGHT / 2,
      anchorOffset: -ROADMAP_ITEM_GRAB_HANDLE_WIDTH / 2,
      anchorSide: operation === 'expandLeading' ? 'inside-left' : 'inside-right',
      // prevent tooltip from showing on touch events
      open: isTouchInteraction ? false : active ? true : undefined,
    })

    const handleTouch = () => setIsTouchInteraction(true)
    const handleTouchEnd = () => {
      setTimeout(() => setIsTouchInteraction(false), 300)
    }

    return (
      <div
        {...testIdProps(`roadmap-view-item-${operation}-handle`)}
        className={clsx(
          operation !== 'move' && styles[operation],
          styles.roadmapItemGrabHandle,
          isTouchInteraction && styles.isTouchInteraction,
          isDisabled && styles.disabled,
        )}
        data-dnd-operation={operation}
        {...props}
        ref={forwardedRef}
        {...contentProps}
        onTouchStart={handleTouch}
        onTouchEnd={handleTouchEnd}
      >
        <div className={clsx(styles.dragHandle, {active})} />
        {portalTooltip}
      </div>
    )
  }),
)

export type RoadmapPillProps = {
  titleHtml: string
  renderLink: boolean
  url: string
  titleColumnValue: ColumnValue<TitleValueWithContentType>
  timeSpan: TimeSpan
  pendingTimeSpan: TimeSpan | null
  number?: number
  assignees?: Array<User>
  allowedOperations: Set<RoadmapDragAndDropOperations>
  dragProps: ReturnType<typeof useRoadmapDragAndDrop>['dragProps']
  dragDetails: ReturnType<typeof useRoadmapDragAndDrop>['dragDetails']
  index: number
  item: MemexItemModel
  mouseState?: MouseState
  expandErrorMessage?: string
}

const defaultAssignees: Array<User> = []
/** Roadmap Pill Implementation - renders the item pill with the position and size determined by the start/end dates */
export const RoadmapPill = memo(function RoadmapPill({
  titleHtml,
  renderLink,
  titleColumnValue,
  timeSpan,
  pendingTimeSpan,
  url,
  number,
  assignees = defaultAssignees,
  allowedOperations,
  dragProps,
  dragDetails,
  index,
  item,
  expandErrorMessage,
}: RoadmapPillProps) {
  const getViewport = useRoadmapGetViewport()
  const {getX} = useRoadmapView()
  const tableWidth = useRoadmapTableWidth()
  const {isVisible} = useIsVisible({ref: dragProps.node})
  const isSavePending = pendingTimeSpan !== null
  const {wrapperProps, focusType, refocusPillArea, onFocusInternalElement, linkRef, onClickPillLink} =
    useRoadmapPillAreaFocus()
  const hasExpandError = Boolean(expandErrorMessage)

  const leadingEdgeRef = useRef<HTMLDivElement | null>(null)
  const {isVisible: isLeadingEdgeVisible} = useIsVisible({ref: leadingEdgeRef})

  // Relative positioning within the visible roadmap viewport.  Add 1 to avoid fractional floating point errors.
  const [startDate, endDate] = pendingTimeSpan
    ? getSortedDates([pendingTimeSpan.start, pendingTimeSpan.end])
    : getSortedDates([timeSpan.start, timeSpan.end])
  const pillOffsetLeft = startDate ? getX(startDate) : 0
  const pillWidth = endDate ? getX(addDays(endDate, 1)) - pillOffsetLeft : 0

  const viewport = getViewport()
  const isBefore = !isVisible && pillOffsetLeft + pillWidth <= viewport.left + 1
  const isAfter = !isVisible && pillOffsetLeft >= viewport.right - 1
  const clearTableFocus = useClearTableFocus()

  const [tooltipPosition, setTooltipPosition] = useState<{left: number; right: number} | undefined>(undefined)

  /** We need to extend the z-index on hovered rows so that the tooltip remains visible on top of other other rows.
   *  We only want to do this when the tooltip is visible, because otherwise table cells can be obscured while hovering
   */
  useBodyClass('roadmap-date-tooltip-visible', tooltipPosition !== undefined)

  useLayoutEffect(() => {
    // refocus pill element after dragging or scrolling, if cell has navigation focus
    void isVisible
    void dragProps.isDragging

    refocusPillArea()
  }, [isVisible, dragProps.isDragging, refocusPillArea])

  const {roadmapTruncateTitles} = useUserSettings()
  const titleSpan = <SanitizedHtml className={styles.SanitizedHtml}>{titleHtml}</SanitizedHtml>
  const canDrag = allowedOperations.has('allowed-drag-update')
  const isDragging = dragProps.isDragging && !!dragDetails
  const ghostPillWidth =
    isDragging && !isSavePending ? dragDetails.dragGhostSpan.endX - dragDetails.dragGhostSpan.startX : 0

  // Attempt to modify the Primer themed box-shadow to avoid any shadowing at the right-side gap of the pill.
  // If found, replace the default '0 3px 6px' with '-3px 6px 6px -3px' (same medium shadow, but not at the right edge)
  // Examples of Primer theme medium shadows:
  //   light.css:  --color-shadow-medium: 0 3px 6px rgba(140, 149, 159, 0.15)
  //   dark_high_contrast.css: --color-shadow-medium: 0 3px 6px #010409
  const {theme} = useTheme()
  let shadow = theme?.shadows.shadow.medium
  if (typeof shadow === 'string' && (shadow.startsWith('0 3px 6px rgb') || shadow.startsWith('0 3px 6px #'))) {
    shadow = shadow.replace('0 3px 6px', '-3px 6px 6px -3px')
  }

  // pillBackground provides the static styling of the pill container with absolute positioning based on start/end dates.
  const pillBackground = (
    <Label
      style={{
        '--roadmap-pill-shadow': shadow,
        width: pillWidth,
      }}
      className={clsx(styles.pillBackground, shadow ? styles.withShadow : null)}
    />
  )

  // visiblePill provides the user interaction of the pill container with absolute positioning based on start/end dates.
  const pillForeground = (
    <Label
      ref={linkRef}
      // The entire visiblePill container serves as a link to open the issue, just like clicking the actual visible link title in pillContent
      as="a"
      href={url}
      target="_blank"
      rel="noreferrer"
      onClick={onClickPillLink}
      onFocus={onFocusInternalElement}
      style={{
        width: isDragging ? ghostPillWidth : pillWidth,
      }}
      {...dragProps.attributes}
      {...dragProps.listeners}
      onMouseOver={e => {
        // Position and resize the tooltip within the pill so that it is centered at mouse entry and always spans the full pill width
        if (isDragging) return
        const box = e.currentTarget.getBoundingClientRect()
        const innerOffset = e.clientX - box.left
        const isPastCenter = e.clientX - box.left > box.width / 2
        const tooltipLeft = isPastCenter ? 0 : innerOffset * 2 - box.width
        const tooltipRight = isPastCenter ? box.width - innerOffset * 2 : 0
        setTooltipPosition({left: tooltipLeft, right: tooltipRight})
      }}
      onMouseOut={() => {
        if (isDragging) return
        setTooltipPosition(undefined)
      }}
      className={clsx(
        'roadmap-pill',
        focusType === PillAreaFocusType.PillLink && 'is-focused',
        styles.pillForeground,
        canDrag && styles.canDrag,
        isDragging && styles.isDragging,
      )}
      {...testIdProps('roadmap-view-item-pill')}
    >
      <Tooltip
        aria-label={getTooltipText(isDragging ? dragDetails.adjustedTimeSpan : timeSpan)}
        className={clsx(
          'roadmap-pill-tooltip',
          dragDetails?.operation === 'move' && 'active',
          styles.Tooltip,
          isDragging && dragDetails.operation !== 'move' && styles.isHidden,
        )}
        // hide pill tooltip when dragging to resize
        style={tooltipPosition}
        direction={index === 0 ? 's' : 'n'}
      />
    </Label>
  )

  // pillContent includes the issue title that might overflow the visiblePill,
  // and gets sticky positioning on the left side when the visiblePill is partially scrolled out of view.
  const pillContent = (
    <>
      <div className={styles.Box_1}>
        <MemexItemIcon titleColumn={titleColumnValue} isBlocked={item.isBlocked()} />
      </div>
      {renderLink ? (
        <Link
          href={url}
          target="_blank"
          rel="noreferrer"
          onClick={onClickPillLink}
          hoverColor="accent.fg"
          draggable={false}
          tabIndex={-1}
          className={clsx(styles.Link, isDragging && styles.isDragging)}
          {...testIdProps('roadmap-item-link')}
        >
          {titleSpan}
        </Link>
      ) : (
        titleSpan
      )}
      {number ? <span className={styles.Text}>{`#${number}`}</span> : null}
      <AssigneeStack
        assignees={assignees}
        style={{zIndex: 'var(--roadmap-pill-assignee-tooltip-z-index)'}}
        avatarProps={{onFocus: clearTableFocus}}
        {...testIdProps(`roadmap-item-assignees`)}
      />
    </>
  )

  const isLeadingActive =
    (dragDetails?.operation === 'expandLeading' && !dragDetails?.swapHandles) ||
    (dragDetails?.operation === 'expandTrailing' && dragDetails?.swapHandles)
  const isTrailingActive =
    (dragDetails?.operation === 'expandTrailing' && !dragDetails?.swapHandles) ||
    (dragDetails?.operation === 'expandLeading' && dragDetails?.swapHandles)

  const oldPositionConditions = isFeatureEnabled('memex_roadmap_drag_style') ? false : isDragging || isSavePending

  return (
    <RoadmapCell
      role="gridcell"
      {...testIdProps('roadmap-pill')}
      data-date-start={startDate ? formatISODateString(startDate) : undefined}
      data-date-end={endDate ? formatISODateString(endDate) : undefined}
      className={clsx(styles.RoadmapCell)}
    >
      <div ref={leadingEdgeRef} role={'presentation'} style={{left: pillOffsetLeft}} className={clsx(styles.Box_2)} />
      <div
        ref={dragProps.setNodeRef}
        style={{left: pillOffsetLeft}}
        className={styles.Box_3}
        {...testIdProps('roadmap-view-pill-background')}
      >
        {pillBackground}
        {!roadmapTruncateTitles.enabled && <div className={styles.pillBackgroundRight} />}
      </div>
      {isDragging && !isSavePending && <TargetPill timeSpan={dragDetails.adjustedTimeSpan} />}
      <div
        // eslint-disable-next-line react-hooks/refs
        ref={dragProps.setNodeRef}
        style={{
          left: pillOffsetLeft,
          // In Safari there is a bug where tooltip is not rendered correctly when the pill is dragged.
          // Adding translateZ(0) forces a repaint and resolved the issue.
          // https://stackoverflow.com/questions/3485365/how-can-i-force-webkit-to-redraw-repaint-to-propagate-style-changes
          transform: `translateX(${
            isDragging && dragDetails.dragGhostSpan.startX ? dragDetails.dragGhostSpan.startX - pillOffsetLeft : 0
          }px) translateZ(0)`,
        }}
        className={styles.Box_5}
        {...wrapperProps}
        {...testIdProps('roadmap-view-pill-foreground')}
      >
        {pillForeground}
        {allowedOperations.has('allowed-drag-expand') && startDate && endDate ? (
          <>
            {isTrailingActive ? null : (
              <RoadmapPillDragHandle
                tooltip={
                  expandErrorMessage ||
                  getTooltipText({
                    start: isDragging ? dragDetails.adjustedTimeSpan.start : timeSpan.start,
                    startIteration: isDragging ? dragDetails.adjustedTimeSpan.startIteration : timeSpan.startIteration,
                    end: undefined,
                  }) ||
                  RoadmapResources.noDateSet
                }
                isDisabled={hasExpandError}
                operation="expandLeading"
                active={isLeadingActive}
                // eslint-disable-next-line react-hooks/refs
                ref={dragProps.setActivatorNodeRef}
                // eslint-disable-next-line react-hooks/refs
                containerRef={dragProps.node}
                // eslint-disable-next-line react-hooks/refs
                {...(!hasExpandError ? dragProps.attributes : {})}
                // eslint-disable-next-line react-hooks/refs
                {...(!hasExpandError ? dragProps.listeners : {})}
                tabIndex={-1}
                aria-label="Resize the item by dragging the earlier date"
              />
            )}
            {isLeadingActive ? null : (
              <RoadmapPillDragHandle
                tooltip={
                  expandErrorMessage ||
                  getTooltipText({
                    start: undefined,
                    end: isDragging ? dragDetails.adjustedTimeSpan.end : timeSpan.end,
                    endIteration: isDragging ? dragDetails.adjustedTimeSpan.endIteration : timeSpan.endIteration,
                  }) ||
                  RoadmapResources.noDateSet
                }
                isDisabled={hasExpandError}
                operation="expandTrailing"
                active={isTrailingActive}
                // eslint-disable-next-line react-hooks/refs
                ref={dragProps.setActivatorNodeRef}
                // eslint-disable-next-line react-hooks/refs
                containerRef={dragProps.node}
                // eslint-disable-next-line react-hooks/refs
                {...(!hasExpandError ? dragProps.attributes : {})}
                // eslint-disable-next-line react-hooks/refs
                {...(!hasExpandError ? dragProps.listeners : {})}
                tabIndex={-1}
                aria-label="Resize the item by dragging the later date"
              />
            )}
          </>
        ) : null}
      </div>
      {isVisible || isDragging ? (
        <div
          style={{
            position:
              oldPositionConditions || isLeadingEdgeVisible || roadmapTruncateTitles.enabled ? 'absolute' : 'sticky',
            left:
              oldPositionConditions || isLeadingEdgeVisible || roadmapTruncateTitles.enabled
                ? pillOffsetLeft + PILL_PADDING
                : tableWidth + PILL_STICKY_MARGIN,
            opacity: isDragging || isSavePending ? 0.3 : undefined,
          }}
          className={styles.Box_6}
          {...testIdProps('roadmap-view-item-pill-content')}
        >
          {roadmapTruncateTitles.enabled ? (
            <Truncate
              title={titleHtml}
              expandable
              style={{
                maxWidth: pillWidth - 16,
              }}
              className={styles.Box_6}
            >
              {pillContent}
            </Truncate>
          ) : (
            pillContent
          )}
        </div>
      ) : isBefore || isAfter ? (
        <NavigationArrowWithFocusContext isBefore={isBefore} startDate={startDate} endDate={endDate} />
      ) : null}
    </RoadmapCell>
  )
})

/**
 * Renders the target timespan that a pill will snap to at the end of a drag operation.
 */
const TargetPill = ({timeSpan}: {timeSpan: TimeSpan}) => {
  const {getX} = useRoadmapView()

  const [startDate, endDate] = getSortedDates([timeSpan.start, timeSpan.end])
  const pillOffsetLeft = startDate ? getX(startDate) : 0
  const pillWidth = endDate ? getX(addDays(endDate, 1)) - pillOffsetLeft : 0

  return (
    <div
      style={{
        width: pillWidth,
        left: pillOffsetLeft,
      }}
      className={clsx(styles.Box_7)}
      {...testIdProps('roadmap-view-pill-drag-target')}
    />
  )
}

try{ RoadmapPillDragHandle.displayName ||= 'RoadmapPillDragHandle' } catch {}
try{ RoadmapPillDragHandle.displayName ||= 'RoadmapPillDragHandle' } catch {}
try{ RoadmapPill.displayName ||= 'RoadmapPill' } catch {}
try{ TargetPill.displayName ||= 'TargetPill' } catch {}